class SearchForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("[name='q']");
    this.resetButton = this.querySelector(".reset-button");
    if (!this.input) return;

    this.input.form.addEventListener("reset", this.onFormReset.bind(this));

    this.input.addEventListener(
      "input",
      this.debounce((event) => {
        this.onChange(event);
      }, 300),
    );

    if (this.hasAttribute("data-pop-panel")) {
      this.addEventListener("focusin", this.onFocusin.bind(this));
      this.addEventListener("focusout", this.onFocusout.bind(this));
    }

    this.addEventListener("speech-result", this.handleSpeechResult.bind(this));
  }

  debounce(func, delay) {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func.apply(this, args), delay);
    };
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    const resetIsHidden = this.resetButton.classList.contains("hidden");
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove("hidden");
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add("hidden");
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  onFormReset(event) {
    event.preventDefault();
    this.input.value = "";
    this.input.focus();

    this.input.dispatchEvent(
      new Event("input", {
        bubbles: true,
        cancelable: true,
      }),
    );
    this.toggleResetButton();
  }

  onFocusin() {
    this.classList.add("in-focus");
    this.isOpen = true;
    this.input.setAttribute("aria-expanded", "true");
    this.toggleHeaderActive(true);
  }

  onFocusout() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;

      this.classList.remove("in-focus");
      this.isOpen = false;
      this.input.setAttribute("aria-expanded", "false");
      this.toggleHeaderActive(false);
    }, 100);
  }

  toggleHeaderActive(active = true) {
    this.header = this.header || this.closest(".section-header");
    if (this.header) this.header.classList.toggle("header--is-active", active);
  }

  handleSpeechResult(event) {
    const { transcript } = event.detail;
    if (!transcript) return;

    this.input.value = transcript;
    this.input.focus();
    this.input.dispatchEvent(
      new Event("input", {
        bubbles: true,
        cancelable: true,
      }),
    );
  }
}

customElements.define("search-form", SearchForm);

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.typeSelector = this.querySelector(
      'select[name="filter.p.product_type"]',
    );
    this.searchTerm = "";
    this.isOpen = false;
  }

  connectedCallback() {
    super.connectedCallback();

    this.searchResultsWrapper = this.querySelector(".search-results-wrapper");

    if (!this.searchResultsWrapper) {
      console.warn("Search results wrapper not found");
      return;
    }

    this.querySelectorAll(".popular-searches a").forEach((suggestion) => {
      suggestion.addEventListener(
        "click",
        this.onPopularKeywordClick.bind(this),
      );
    });

    this.abortController = new AbortController();
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();

    const currentSearchTerm = this.getQuery();
    if (currentSearchTerm === this.searchTerm || !currentSearchTerm) return;

    this.searchTerm = currentSearchTerm;

    if (this.input.value.trim().length <= 0) {
      return this.closeResults(true);
    }

    this.showPlaceholder();
    this.getSearchResults();
  }

  onPopularKeywordClick(event) {
    event.preventDefault();

    const keyword = event.currentTarget.getAttribute("data-keyword");
    if (keyword) {
      this.input.value = keyword;
      this.input.focus();
      this.input.dispatchEvent(
        new Event("input", {
          bubbles: true,
          cancelable: true,
        }),
      );
    }
  }

  onFormReset(event) {
    super.onFormReset(event);
    this.searchTerm = "";

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = new AbortController();
    }

    this.closeResults(true);
  }

  onFocusin() {
    super.onFocusin();

    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.showResults();
    } else if (currentSearchTerm.length > 0) {
      this.getSearchResults();
    }
  }

  showPlaceholder() {
    if (!this.searchResultsWrapper) return;

    const placeholderTemplate = this.querySelector(
      `#Placeholder-Search-Form-${this.dataset.section}`,
    );

    if (placeholderTemplate) {
      this.placeholder = this.placeholder || placeholderTemplate.innerHTML;
      this.searchResultsWrapper.innerHTML = this.placeholder;
    }
  }

  getSearchResults() {
    // Use the HTML endpoint with section rendering
    const baseUrl = window.Shopify?.routes?.root || "/";
    // Use 'predictive-search' as the section_id (this is the section file name)
    const sectionId = "predictive-search";

    const searchUrl = baseUrl + "search/suggest";

    const queryKey = this.input.value.trim().replace(/\s+/g, "-").toLowerCase();
    this.setLiveRegionLoading();

    // Check cache first
    if (this.cachedResults[queryKey]) {
      return this.renderSearchResults(this.cachedResults[queryKey]);
    }

    // Build URL with correct parameters per Shopify documentation
    const params = new URLSearchParams();
    params.set("q", this.input.value.trim());
    params.set("resources[limit]", "10");
    params.set("resources[limit_scope]", "each");
    params.set(
      "resources[options][fields]",
      "title,product_type,variants.title,variants.sku,variants.barcode",
    );
    params.set("section_id", sectionId);

    const fullUrl = searchUrl + "?" + params.toString();

    ("Fetching from:", fullUrl);

    fetch(fullUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        try {
          // Parse the HTML response and extract the section content
          const parser = new DOMParser();
          const doc = parser.parseFromString(text, "text/html");
          const section = doc.querySelector(
            "#shopify-section-predictive-search",
          );

          if (!section) {
            console.warn("No predictive search section found in response");
            this.searchResultsWrapper.innerHTML = "<p>No results found</p>";
            return;
          }

          const resultsMarkup = section.innerHTML;
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        } catch (parseError) {
          console.error("Error parsing search results:", parseError);
          this.searchResultsWrapper.innerHTML = "<p>Error loading results</p>";
        }
      })
      .catch((error) => {
        if (error?.name === "AbortError") {
          return; // Request was cancelled
        }
        console.error("Search error:", error);
        this.removeAttribute("loading");
        this.searchResultsWrapper.innerHTML = "";
        this.removeAttribute("results");
      });
  }

  renderSearchResults(resultsHtml) {
    if (!this.searchResultsWrapper) return;

    this.searchResultsWrapper.innerHTML = resultsHtml;
    this.setAttribute("results", "true");
    this.setLiveRegionResults();
    this.showResults();
  }

  getResultsMaxHeight() {
    const predictiveMain = this.querySelector(".predictive-search-main");
    if (!predictiveMain) return window.innerHeight * 0.6;

    this.resultsMaxHeight =
      window.innerHeight - predictiveMain.getBoundingClientRect().top - 32;

    return this.resultsMaxHeight;
  }

  showResults() {
    this.style.setProperty(
      "--result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`,
    );
    this.setAttribute("open", "true");
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      if (this.searchResultsWrapper) {
        this.searchResultsWrapper.innerHTML = "";
      }
      this.removeAttribute("results");
    }

    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.resultsMaxHeight = false;
    this.style.removeProperty("--result-max-height");
  }

  setLiveRegionLoading() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text") || "Loading";

    this.setAttribute("loading", "true");
    this.setLiveRegionText(this.loadingText);
  }

  setLiveRegionResults() {
    const countElement = this.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );
    const countText = countElement?.textContent || "Results loaded";
    this.removeAttribute("loading");
    this.setLiveRegionText(countText);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement?.setAttribute("aria-hidden", "true");
    }, 1000);
  }
}

customElements.define("predictive-search", PredictiveSearch);
